import React from "react";
import { AppContext } from "./genesys.context";
import {
  hasPassedTeaserThreshold,
  type ScrollSubscription,
} from "../utils/teaserReveal";

/**
 * Whether the launcher teaser may be shown yet.
 *
 * Kept apart from `showMinimizeBotPopup`, which records that the visitor has
 * dismissed the teaser and is persisted. This flag is per page view and never
 * persisted. Without a provider it defaults to `true`, so anything rendered
 * outside `GenesysChatbot` (component tests, for one) keeps the teaser as is.
 */
export const TeaserRevealContext = React.createContext<boolean>(true);

export const useTeaserRevealed = (): boolean =>
  React.useContext(TeaserRevealContext);

interface TeaserRevealProviderProps {
  /** Where scroll positions come from — the window, or the iframe host page. */
  subscribe: ScrollSubscription;
  children: React.ReactNode;
}

const TeaserRevealProvider = ({
  subscribe,
  children,
}: TeaserRevealProviderProps): React.JSX.Element => {
  const {
    showMinimizeBotPopup,
    hideLauncher,
  }: { showMinimizeBotPopup: boolean; hideLauncher: boolean } =
    React.useContext(AppContext);

  const [revealed, setRevealed] = React.useState<boolean>(false);

  // Nothing to wait for once revealed, or when the teaser will not show anyway.
  const watching = !revealed && showMinimizeBotPopup && !hideLauncher;

  React.useEffect(() => {
    if (!watching) return;

    return subscribe((snapshot) => {
      if (hasPassedTeaserThreshold(snapshot)) setRevealed(true);
    });
  }, [watching, subscribe]);

  return (
    <TeaserRevealContext.Provider value={revealed}>
      {children}
    </TeaserRevealContext.Provider>
  );
};

export default TeaserRevealProvider;
